import { z } from "zod";
import { CREATIVE_TEMPLATE_MAX_NODES } from "../../../utils/constants";

const httpUrlSchema = z
  .string()
  .min(1)
  .refine((value) => !value.startsWith("data:"), {
    message: "dataUrlNotAllowed",
  });

const toStringList = (value) => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value;
  return Object.keys(value)
    .sort((left, right) => Number(left) - Number(right))
    .map((key) => value[key]);
};

const stringListSchema = z
  .union([z.array(z.string()), z.record(z.string())])
  .optional()
  .transform(toStringList);

const sourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("fixed"),
    value: z.string(),
  }),
  z.object({
    kind: z.literal("asset"),
    assetRef: z.string().nullable().optional(),
  }),
  z.object({
    kind: z.literal("data"),
    variable: z.string().min(1),
  }),
  z.object({
    kind: z.literal("computed"),
    expression: z.string().min(1),
  }),
  z.object({
    kind: z.literal("generated"),
    generator: z.enum(["image", "text"]).optional(),
    promptTemplate: z.string().optional(),
    negativePrompt: z.string().optional(),
    referenceAssets: stringListSchema,
    seedStrategy: z.string().optional(),
  }),
]);

const nodeLayoutSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  zIndex: z.number().optional(),
  visible: z.boolean().optional(),
  rotation: z.number().optional(),
});

const TEXT_KINDS = ["fixed", "data", "computed", "generated"];
const IMAGE_KINDS = ["asset", "data", "generated"];

const refineNodeCombos = ({ node, ctx, path }) => {
  const sourceKind = node.source?.kind;
  if (node.type === "text") {
    if (!sourceKind || !TEXT_KINDS.includes(sourceKind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "illegalSourceCombo",
        path: [...path, "source", "kind"],
      });
    }
  }
  if (node.type === "image") {
    if (!sourceKind || !IMAGE_KINDS.includes(sourceKind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "illegalSourceCombo",
        path: [...path, "source", "kind"],
      });
    }
  }
  if (node.type === "group" && sourceKind) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "illegalSourceCombo",
      path: [...path, "source", "kind"],
    });
  }
};

const nodeSchema = z.lazy(() =>
  z
    .object({
      id: z.string().min(1),
      name: z.string().min(1),
      type: z.enum(["text", "image", "shape", "group"]),
      source: sourceSchema.optional(),
      style: z
        .object({
          fontAssetRef: z.string().optional(),
          fontSize: z.number().optional(),
          fontWeight: z.number().optional(),
          lineHeight: z.number().optional(),
          letterSpacing: z.number().optional(),
          color: z.string().optional(),
          align: z.enum(["left", "center", "right"]).optional(),
          orientation: z.enum(["horizontal", "vertical"]).optional(),
          transform: z.string().optional(),
        })
        .optional(),
      overflow: z
        .object({
          strategy: z.enum(["shrink", "reject"]).optional(),
          minimumFontSize: z.number().optional(),
          minimumLines: z.number().optional(),
          maximumLines: z.number().optional(),
          onFailure: z.string().optional(),
        })
        .optional(),
      shape: z
        .object({
          kind: z.literal("roundedRectangle"),
          radius: z.number().optional(),
          fill: z.string().optional(),
        })
        .optional(),
      fit: z.enum(["cover", "contain"]).optional(),
      constraints: z
        .object({
          minimumWidth: z.number().optional(),
          safeArea: z.number().optional(),
          lockAspectRatio: z.boolean().optional(),
          assetIntegrity: z.boolean().optional(),
          allowRecolor: z.boolean().optional(),
          protectedByNodes: stringListSchema,
        })
        .optional(),
      children: z.record(z.lazy(() => nodeSchema)).optional(),
      childOrder: stringListSchema,
      componentFrame: z
        .object({
          width: z.number().positive(),
          height: z.number().positive(),
        })
        .optional(),
      clipContent: z.boolean().optional(),
      isBackground: z.boolean().optional(),
      layout: nodeLayoutSchema.optional(),
    })
    .superRefine((node, ctx) => {
      refineNodeCombos({ node, ctx, path: [] });
    })
);

const backdropAssetSchema = z.object({
  type: z.literal("image"),
  assetID: z.string().min(1),
  mimeType: z.string().min(1),
  url: httpUrlSchema,
  width: z.number().positive(),
  height: z.number().positive(),
  role: z.literal("editorBackdrop"),
});

const composeAssetSchema = z.object({
  type: z.enum(["image", "font"]),
  assetID: z.string().min(1),
  mimeType: z.string().min(1),
  url: httpUrlSchema.optional(),
  version: z.number().optional(),
  checksum: z.string().optional(),
  immutable: z.boolean().optional(),
});

const variableSchema = z.object({
  label: z.string().min(1),
  type: z.enum(["string", "number", "boolean", "image", "color", "date"]),
  required: z.boolean().optional(),
  editable: z.boolean().optional(),
  constraints: z.record(z.any()).optional(),
  resolution: z.record(z.any()).optional(),
});

const countNodes = ({ nodes = {} }) =>
  Object.values(nodes).reduce((total, node) => {
    return total + 1 + countNodes({ nodes: node.children || {} });
  }, 0);

const collectNodeIds = ({ nodes = {}, acc = [] }) => {
  Object.values(nodes).forEach((node) => {
    acc.push(node.id);
    collectNodeIds({ nodes: node.children || {}, acc });
  });
  return acc;
};

export const creativeTemplateDocSchema = z
  .object({
    schemaVersion: z.string().min(1),
    id: z.string().min(1),
    advertiserID: z.string().min(1),
    name: z.string().min(1),
    status: z.enum(["draft", "approved", "archived"]),
    editor: z.object({
      referenceImageAssetID: z.string().min(1).nullish(),
      activeArtboardID: z.string().min(1),
      analysis: z
        .object({
          status: z.enum(["processing", "success", "error"]),
          jobID: z.string().min(1).nullish(),
          errorCode: z.string().nullish(),
        })
        .optional(),
    }),
    assets: z
      .record(z.union([backdropAssetSchema, composeAssetSchema]))
      .default({}),
    variables: z.record(variableSchema).default({}),
    nodes: z.record(nodeSchema).default({}),
    artboards: z
      .record(
        z.object({
          label: z.string().optional(),
          width: z.number().positive(),
          height: z.number().positive(),
          referenceAssetRef: z.string().min(1).optional(),
          nodes: z.record(nodeSchema).optional(),
          nodeLayouts: z.record(nodeLayoutSchema).default({}),
        })
      )
      .refine((map) => Object.keys(map || {}).length >= 1, {
        message: "artboardRequired",
      }),
    audit: z.object({
      createdBy: z.string().optional(),
      creationTime: z.string().optional(),
      updateTime: z.string().optional(),
    }),
  })
  .superRefine((doc, ctx) => {
    const sharedNodes = doc.nodes || {};
    const artboards = doc.artboards || {};
    const activeArtboardID = doc.editor?.activeArtboardID;

    if (activeArtboardID && !artboards[activeArtboardID]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "unknownActiveArtboard",
        path: ["editor", "activeArtboardID"],
      });
    }

    Object.entries(artboards).forEach(([artboardID, artboard]) => {
      const artboardNodes =
        artboard?.nodes != null ? artboard.nodes : sharedNodes;
      if (countNodes({ nodes: artboardNodes }) > CREATIVE_TEMPLATE_MAX_NODES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "nodeCapExceeded",
          path: ["artboards", artboardID, "nodes"],
        });
      }
      Object.keys(artboard?.nodeLayouts || {}).forEach((layoutID) => {
        if (!artboardNodes[layoutID]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "orphanLayout",
            path: ["artboards", artboardID, "nodeLayouts", layoutID],
          });
        }
      });
      const referenceRef = artboard?.referenceAssetRef;
      if (referenceRef && !doc.assets?.[referenceRef]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "orphanReference",
          path: ["artboards", artboardID, "referenceAssetRef"],
        });
      }
      const artboardIds = collectNodeIds({ nodes: artboardNodes });
      artboardIds.forEach((nodeID, index, allIds) => {
        if (allIds.indexOf(nodeID) !== index) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "duplicateNodeId",
            path: ["artboards", artboardID, "nodes"],
          });
        }
      });
      Object.keys(artboardNodes).forEach((rootID) => {
        if (artboardNodes[rootID]?.layout) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "rootLayoutOnNode",
            path: ["artboards", artboardID, "nodes", rootID, "layout"],
          });
        }
      });
    });

    Object.entries(doc.assets || {}).forEach(([key, asset]) => {
      const isBackdropKey = key === "reference" || key.startsWith("reference_");
      if (!isBackdropKey && asset?.role === "editorBackdrop") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "backdropNotCompose",
          path: ["assets", key, "role"],
        });
      }
    });
  });

export const parseCreativeTemplateDoc = (doc) =>
  creativeTemplateDocSchema.safeParse(doc);
